import { useMemo } from 'react';
import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { locationKeys } from '@/lib/queryKeys';
import type { ListLocationsParams, Location, LocationListResponse } from '@/types/location';

/**
 * Canonicalize the params BEFORE they are used for either the cache key or the
 * request, so the two can never disagree.
 *
 * Trimming inside the request builder alone meant `'acme '` and `'acme'` hashed
 * to different React Query keys while producing byte-identical requests — two
 * cache entries and a redundant fetch for the same search. An empty-after-trim
 * search is dropped entirely so `''` and `undefined` are also one key.
 */
export function normalizeLocationParams(params: ListLocationsParams): ListLocationsParams {
  const search = params.search?.trim();
  return {
    ...params,
    search: search ? search : undefined,
  };
}

function buildQuery(params: ListLocationsParams): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.customerId) qs.set('customerId', params.customerId);
  if (params.status) qs.set('status', params.status);
  if (params.isService !== undefined) qs.set('isService', String(params.isService));
  if (params.includeContacts) qs.set('includeContacts', 'true');
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** Tenant-wide location list — the "places we work" index. */
export function useLocations(params: ListLocationsParams = {}) {
  const normalized = useMemo(() => normalizeLocationParams(params), [params]);

  return useApiQuery<LocationListResponse>({
    queryKey: locationKeys.list(normalized as Record<string, unknown>),
    queryFn: () =>
      apiGet<LocationListResponse>(operationsClient, `/locations${buildQuery(normalized)}`),
  });
}

export function useLocation(
  locationId: string | undefined,
  options?: { showErrorToast?: boolean },
) {
  return useApiQuery<Location>({
    queryKey: locationKeys.detail(locationId ?? ''),
    queryFn: () => apiGet<Location>(operationsClient, `/locations/${locationId}`),
    enabled: Boolean(locationId),
    showErrorToast: options?.showErrorToast,
  });
}
